import React from "react";
import { useNavigate } from "react-router-dom";
import TierBadge from "./TierBadge";
import ProgressBar, { ProgressRing } from "./ProgressBar";

/**
 * ConceptCard Component
 * Displays a concept with progress, tier, and action buttons
 */
const ConceptCard = ({ progress, onStartLearning }) => {
  const navigate = useNavigate();

  const handleStartLearning = () => {
    if (onStartLearning) {
      onStartLearning(progress);
    } else {
      navigate(`/adaptive-learning/session/${progress.conceptCode}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-800 group hover:-translate-y-1">
      {/* Header with Tier Badge */}
      <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {progress.conceptName}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {progress.subject} • Grade {progress.grade}
            </p>
          </div>
          <TierBadge tier={progress.progressTier} size="md" showLabel={false} />
        </div>
        
        {/* Tier Badge with label */}
        <TierBadge tier={progress.progressTier} size="sm" />
      </div>

      {/* Progress Section */}
      <div className="p-6">
        {/* Circular Progress */}
        <div className="flex justify-center mb-6">
          <ProgressRing
            percentage={progress.masteryPercentage}
            tier={progress.progressTier}
            size={140}
            strokeWidth={10}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {progress.totalAttempts || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Attempts
            </div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {progress.totalQuestionsAnswered || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Questions
            </div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {progress.totalCorrectAnswers || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Correct
            </div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
              {(progress.averageScore || 0).toFixed(0)}%
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Avg Score
            </div>
          </div>
        </div>

        {/* Last Attempt Date */}
        {progress.lastAttemptDate && (
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            Last attempt: {formatDate(progress.lastAttemptDate)}
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleStartLearning}
          className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          {progress.totalAttempts > 0 ? "Continue Learning" : "Start Learning"}
        </button>

        {/* Recommended Questions */}
        {progress.recommendedQuestions && (
          <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
            Recommended: {progress.recommendedQuestions} questions
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ConceptCardCompact Component
 * Smaller card variant for lists
 */
export const ConceptCardCompact = ({ progress, onStartLearning }) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-800 flex items-center gap-4">
      {/* Progress Ring - Smaller */}
      <ProgressRing
        percentage={progress.masteryPercentage}
        tier={progress.progressTier}
        size={80}
        strokeWidth={6}
      />

      {/* Info Section */}
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-gray-900 dark:text-white truncate">
          {progress.conceptName}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {progress.subject} • {progress.totalAttempts} attempts
        </p>
        <TierBadge tier={progress.progressTier} size="sm" className="mt-2" />
      </div>

      {/* Action Button */}
      <button
        onClick={() => onStartLearning && onStartLearning(progress)}
        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
      >
        Start
      </button>
    </div>
  );
};

export default ConceptCard;


